import { useEffect, useRef, useState } from 'react';

interface IProps {
    children?: React.ReactNode;
    hasNextPage?: boolean;
    isLoading?: boolean;
    scrollElement?: HTMLElement; // Determines the element to check for scrolling, instead of the window
    shouldRunCallback?: boolean;
    onScrollBottom?(): void;
}

export const InfiniteScroll = ({
    children,
    scrollElement,
    shouldRunCallback,
    onScrollBottom,
}: IProps) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [hitBottom, setHitBottom] = useState(false);

    const hasReachedBottom = () => {
        if (!contentRef.current) return false;

        if (scrollElement) {
            // Use a small tolerance to account for floating-point precision issues
            const tolerance = 1; // 1px tolerance
            const scrollBottom =
                scrollElement.scrollHeight - scrollElement.offsetHeight;

            return (
                Math.abs(scrollElement.scrollTop - scrollBottom) <= tolerance
            );
        }

        return (
            Math.ceil(window.innerHeight + window.scrollY) ===
            document.body.offsetHeight
        );
    };

    useEffect(() => {
        const handleScroll = () => {
            const reachedBottom = hasReachedBottom();

            if (reachedBottom === hitBottom) return;

            setHitBottom(reachedBottom);
        };

        const element = scrollElement || document;

        element.addEventListener('scroll', handleScroll);

        return () => {
            element.removeEventListener('scroll', handleScroll);
        };
    });

    useEffect(() => {
        if (!shouldRunCallback || !hitBottom || !onScrollBottom) return;

        if (!hasReachedBottom()) return;

        setHitBottom(false);

        onScrollBottom();
    }, [hitBottom, shouldRunCallback, onScrollBottom]);

    return <div ref={contentRef}>{children}</div>;
};
